import { Box } from '@mui/material';
import React, { useState, useMemo } from 'react';
import { Image, StyleSheet, TouchableOpacity } from 'react-native';
import { useStoreConfig } from '../contexts/StoreConfigContext';
import { useCachedImage } from '../app/hooks/custom/useCachedImage';

type ResizeMode = 'cover' | 'contain' | 'center' | 'stretch' | 'repeat';

interface ImageData {
    url: string;
    resizeMode?: string;
}

interface ImagePreviewerProps {
    images: { [key: string]: ImageData };
    defaultResizeMode?: ResizeMode;
}

interface ThumbnailImageProps {
    imageUrl: string;
    isSelected: boolean;
    onPress: () => void;
    resizeMode: ResizeMode;
    primaryColor: string;
}

const ThumbnailImage: React.FC<ThumbnailImageProps> = ({ imageUrl, isSelected, onPress, resizeMode, primaryColor }) => {
    const { imageSource } = useCachedImage(imageUrl);

    if (!imageSource) return null;

    return (
        <TouchableOpacity onPress={onPress}>
            <Image
                source={imageSource}
                style={[
                    styles.thumbnail,
                    isSelected && { borderColor: primaryColor, borderWidth: 2 }
                ]}
                resizeMode={resizeMode}
            />
        </TouchableOpacity>
    );
};

export const ImagePreviewer: React.FC<ImagePreviewerProps> = ({ images, defaultResizeMode = 'cover' }) => {
    const { config } = useStoreConfig();

    // Sort images to ensure thumbnail is first, then show all other images in order
    const sortedImages = useMemo(() => {
        const entries = Object.entries(images);
        const thumbnail = entries.find(([key]) => key === 'thumbnail');
        const others = entries.filter(([key]) => key !== 'thumbnail');

        const result = thumbnail ? [thumbnail, ...others] : entries;
        return result;
    }, [images]);

    const [selectedImageKey, setSelectedImageKey] = useState<string>(sortedImages[0]?.[0] || '');
    
    // Get the selected image data
    const selectedImage = useMemo(() => {
        return images[selectedImageKey] || sortedImages[0]?.[1];
    }, [images, selectedImageKey, sortedImages]);
    
    const selectedResizeMode = (selectedImage?.resizeMode as ResizeMode) || defaultResizeMode;
    const { imageSource: mainImageSource } = useCachedImage(selectedImage?.url);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start' }}>
            {/* Thumbnail column on the left */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {sortedImages.map(([key, image]) => {
                    // Get per-image resizeMode, falling back to default
                    const imageResizeMode = (image.resizeMode as ResizeMode) || defaultResizeMode;
                    return (
                    <ThumbnailImage
                        key={key}
                        imageUrl={image.url}
                            isSelected={selectedImageKey === key}
                            onPress={() => setSelectedImageKey(key)}
                            resizeMode={imageResizeMode}
                        primaryColor={config.primaryColor}
                    />
                    );
                })}
            </Box>

            {/* Main image on the right */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {mainImageSource && (
                    <Image 
                        source={mainImageSource}
                        style={styles.mainImage}
                        resizeMode={selectedResizeMode}
                    />
                )}
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    mainImage: {
        width: '100%',
        maxWidth: 600,
        aspectRatio: 1 / 1,
        borderRadius: 12,
        backgroundColor: '#f5f5f5',
    },
    thumbnail: {
        width: 100,
        height: 100,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        backgroundColor: '#f5f5f5',
    },
});
